import { reactive, ref, onMounted, onBeforeUnmount } from 'vue'

/**
 * Torna um elemento arrastável via mouse/toque.
 *
 * Uso:
 *   const { pos, isDragging, startDrag } = useDraggable()
 *   <div :style="{ top: pos.top + 'px', left: pos.left + 'px' }"
 *        @mousedown="startDrag" @touchstart="startDrag">
 *
 * O arrasto é disparado a partir do elemento que recebe o evento; interações
 * em `button`, `a`, `input`, etc. (ou qualquer elemento com `data-no-drag`)
 * não iniciam o arrasto, permitindo cliques normais dentro do cabeçalho.
 */
export function useDraggable() {
    const pos = reactive({ top: 0, left: 0 })
    const isDragging = ref(false)

    let dragOffset = { x: 0, y: 0 }

    function startDrag(event) {
        const target = event.target
        if (target && target.closest && target.closest('button, a, input, textarea, select, [data-no-drag]')) return

        isDragging.value = true

        const clientX = event.touches ? event.touches[0].clientX : event.clientX
        const clientY = event.touches ? event.touches[0].clientY : event.clientY
        dragOffset = {
            x: clientX - pos.left,
            y: clientY - pos.top,
        }

        event.preventDefault()
    }

    function moveDrag(event) {
        if (!isDragging.value) return

        const clientX = event.touches ? event.touches[0].clientX : event.clientX
        const clientY = event.touches ? event.touches[0].clientY : event.clientY

        pos.top = Math.max(0, Math.min(clientY - dragOffset.y, window.innerHeight - 40))
        pos.left = Math.max(0, Math.min(clientX - dragOffset.x, window.innerWidth - 40))
    }

    function endDrag() {
        isDragging.value = false
    }

    onMounted(() => {
        document.addEventListener('mousemove', moveDrag)
        document.addEventListener('mouseup', endDrag)
        document.addEventListener('touchmove', moveDrag, { passive: false })
        document.addEventListener('touchend', endDrag)
    })

    onBeforeUnmount(() => {
        document.removeEventListener('mousemove', moveDrag)
        document.removeEventListener('mouseup', endDrag)
        document.removeEventListener('touchmove', moveDrag)
        document.removeEventListener('touchend', endDrag)
    })

    return { pos, isDragging, startDrag }
}
